/**
 * Catalog metadata beyond the ids: the economy of every game and the lobby section it lives in
 * (docs/four-games-mobile-plan.md, owner decision 2026-10-01 «всё за токены, в нашем стиле»).
 *
 * Economy — who pays whom:
 *   house — a game against the house with a target RTP (slots, Liftoff, Carp, Pot, Roulette, Red Envelopes …).
 *           Only these have `rtp`, take part in the daily quest and in the RTP report against a target.
 *   pvp   — people play against people for a common bank of equal stakes (mahjong tables, Dou Dizhu, chess):
 *           the winner takes the bank minus the house fee `feeBps`; a draw / a bot's win refunds. No RTP.
 *   pool  — a prize-pool competition (puzzles): every entrant pays the same entry for ONE challenge of a window
 *           (hour / UTC day), the fund = Σ entries − `feeBps`, prizes go to the top by the result the server
 *           re-plays (apps/server/src/skill). No fixed «solved → ×N» payout and no stake against a bot: a solver or an
 *           engine can never make the house lose — players only compete for each other's entries.
 *   Training (free practice, `training: true`) exists beside the paid mode of skill games: no stake, no reward, no XP,
 *   no passport stamp.
 *
 * The jackpot contribution of pvp / pool stakes comes from the fee (`JACKPOT_PEOPLE_GAMES` on the server), the daily
 * quest is house-only (quests.ts), the passport stamps pvp / pool skill games for a confirmed finish, not for a stake.
 */
import type { GameId } from './common';

export const GAME_ECONOMIES = ['house', 'pvp', 'pool'] as const;
export type GameEconomy = (typeof GAME_ECONOMIES)[number];

/** Lobby sections in display order (the filters «Originals», «Tables», «Slots», «Machines», «Puzzles», «Strategy») */
export const GAME_SECTIONS = ['originals', 'puzzles', 'strategy', 'tables', 'slots', 'machines'] as const;
export type GameSection = (typeof GAME_SECTIONS)[number];

/** The four skill games of wave A–D. «Puzzles» are pool competitions, chess is pvp. */
export const SKILL_GAME_IDS = ['tile-match', 'color-sort', 'daily-words', 'chess'] as const satisfies readonly GameId[];
export type SkillGameId = (typeof SKILL_GAME_IDS)[number];
export const PUZZLE_IDS = ['tile-match', 'color-sort', 'daily-words'] as const satisfies readonly SkillGameId[];
export type PuzzleId = (typeof PUZZLE_IDS)[number];
export const STRATEGY_IDS = ['chess'] as const satisfies readonly SkillGameId[];
export type StrategyId = (typeof STRATEGY_IDS)[number];

export const isSkillGame = (g: string): g is SkillGameId => (SKILL_GAME_IDS as readonly string[]).includes(g);
export const isPuzzleGame = (g: string): g is PuzzleId => (PUZZLE_IDS as readonly string[]).includes(g);

export type EconomyMeta =
  | { economy: 'house' }
  /** `feeBps`: house fee of a won bank, basis points */
  | { economy: 'pvp'; feeBps: number }
  /** `feeBps`: house fee of the fund, basis points; `entries`: entry fees offered (tokens, ascending) */
  | { economy: 'pool'; feeBps: number; entries: readonly number[] };

/** Fee of the puzzle competitions (owner decision 2026-10-01): 10 % of the entries. */
export const POOL_FEE_BPS = 1000;
/** Entry fees of a puzzle competition window (lucky numbers, no digit 4) */
export const POOL_ENTRIES = [18, 88] as const;
/** Fee of a chess game for a stake (like mahjong tables / Dou Dizhu): 5 % of the bank */
export const CHESS_FEE_BPS = 500;
/** Stakes of chess tables (like mahjong tables) */
export const CHESS_STAKES = [18, 88, 888] as const;

const POOL: EconomyMeta = { economy: 'pool', feeBps: POOL_FEE_BPS, entries: POOL_ENTRIES };

/** Every game's economy. A new game is one line here (typecheck fails until it is added). */
export const GAME_ECONOMY: Record<GameId, EconomyMeta> = {
  crash: { economy: 'house' },
  pot: { economy: 'house' },
  roulette: { economy: 'house' },
  garden: { economy: 'house' },
  deep: { economy: 'house' },
  spaceport: { economy: 'house' },
  'sweet-shop': { economy: 'house' },
  workshop: { economy: 'house' },
  'koi-pond': { economy: 'house' },
  'lucky-lever': { economy: 'house' },
  'neon-arcade': { economy: 'house' },
  'pirate-cove': { economy: 'house' },
  carp: { economy: 'house' },
  firecrackers: { economy: 'house' },
  hongbao: { economy: 'house' },
  mahjong: { economy: 'house' },
  zhajinhua: { economy: 'house' },
  // the fee of the bank is the margin (packages/games mahjong-table / doudizhu config feeBps)
  'mahjong-table': { economy: 'pvp', feeBps: 500 },
  doudizhu: { economy: 'pvp', feeBps: 500 },
  'tile-match': POOL,
  'color-sort': POOL,
  'daily-words': POOL,
  chess: { economy: 'pvp', feeBps: CHESS_FEE_BPS },
};

export const economyOf = (g: GameId): GameEconomy => GAME_ECONOMY[g].economy;
export const isHouseGame = (g: GameId): boolean => GAME_ECONOMY[g].economy === 'house';
/** house fee in basis points of a pvp / pool game; null for a house game (it has an RTP instead) */
export const feeBpsOf = (g: GameId): number | null => {
  const e = GAME_ECONOMY[g];
  return e.economy === 'house' ? null : e.feeBps;
};

/** Games with a free practice mode (no stake, no reward): Dou Dizhu training tables and every skill game */
export const TRAINING_GAMES: ReadonlySet<GameId> = new Set<GameId>(['doudizhu', ...SKILL_GAME_IDS]);

/** The lobby section of every game. */
export const GAME_SECTION: Record<GameId, GameSection> = {
  crash: 'originals',
  pot: 'originals',
  roulette: 'originals',
  carp: 'originals',
  firecrackers: 'originals',
  mahjong: 'originals',
  zhajinhua: 'originals',
  hongbao: 'tables',
  'mahjong-table': 'tables',
  doudizhu: 'tables',
  garden: 'slots',
  deep: 'slots',
  spaceport: 'slots',
  'sweet-shop': 'slots',
  workshop: 'slots',
  'koi-pond': 'slots',
  'lucky-lever': 'machines',
  'neon-arcade': 'machines',
  'pirate-cove': 'machines',
  'tile-match': 'puzzles',
  'color-sort': 'puzzles',
  'daily-words': 'puzzles',
  chess: 'strategy',
};

/** URL prefix of a section below /:lang (slots and machines share `/slots/<id>`; originals and tables live at `/<id>`) */
export const SECTION_PATH: Partial<Record<GameSection, string>> = {
  slots: 'slots',
  machines: 'slots',
  puzzles: 'puzzles',
  strategy: 'strategy',
};

/** "/slots/garden", "/puzzles/tile-match", "/strategy/chess", "/crash" — the game's path below /:lang */
export const gamePathOf = (g: GameId): string => {
  const prefix = SECTION_PATH[GAME_SECTION[g]];
  return prefix ? `/${prefix}/${g}` : `/${g}`;
};
